// Embed-code builders for the Wind Widgets page, ported from WindAlert-Web
// App_Common/JS/Pages/WindWidgets.aspx.js. Each builder returns the widget's iframe src;
// buildIframeCode wraps it in the embed markup the page hands to users.
// Parameter names, order and defaults match the legacy page.

// The widgets take the temperature unit as "F" or "C"
function temperatureUnit(unitsTemp) {
  return (unitsTemp || '').toLowerCase() === 'c' ? 'C' : 'F';
}

function stripHash(color) {
  return (color || '').replace('#', '');
}

function appendLocation(params, { spotId, favoriteSpotId, lat, lon }) {
  if (spotId > 0) {
    params.append('spot_id', spotId);
  }
  else if (favoriteSpotId > 0) { 
    params.append('fav_spot_id', favoriteSpotId);
  }
  else if (lat !== undefined && lat !== null) {
    params.append('lat', lat);
    params.append('lon', lon);
  }
}

function appendCommon(params, { color, name, primaryActivity, widgetApp }) {
  if (color) params.append('color', stripHash(color));
  if (name) params.append('name', name);
  if (primaryActivity) params.append('activity', primaryActivity);

  params.append('app', widgetApp);
}

// The parameters both map widgets end with: units, activity, the saved view and the app
function appendMapTail(params, { unitsWind, unitsTemp, primaryActivity, center, zoom, widgetApp }) {
  if (unitsWind && unitsWind !== 'mph') params.append('u_w', unitsWind);
  // Always sent: legacy meant to skip the default, but its check compared the temperature unit
  // against "mph" (copied from the wind check), so it never did. Kept so the URLs match legacy's.
  params.append('u_t', temperatureUnit(unitsTemp));
  if (primaryActivity) params.append('act', primaryActivity);

  if (center && zoom) {
    params.append('m_c', center);
    params.append('m_z', zoom);
  }

  params.append('app', widgetApp);
}

function appendMapSearch(params, { lat, lon, spotName, spotId, favoriteSpotId }) {
  params.append('search', `${lat},${lon}`);
  params.append('sn', spotName);

  if (spotId > 0) params.append('sid', spotId);
  else if (favoriteSpotId > 0) params.append('fsid', favoriteSpotId);
}

export function buildCurrentConditionsUrl(options) {
  const { widgetUrl, unitsWind, unitsTemp, width, height } = options;
  const params = new URLSearchParams();

  appendLocation(params, options);
  params.append('units_wind', unitsWind);
  params.append('units_temp', temperatureUnit(unitsTemp));
  params.append('width', width);
  params.append('height', height);
  appendCommon(params, options);

  return `${widgetUrl}/widgets/web/conditions?${params.toString()}`;
}

export function buildYesterdaysWindUrl(options) {
  const { widgetUrl, unitsWind, unitsTemp, width, height } = options;
  const params = new URLSearchParams();

  appendLocation(params, options);
  params.append('day_start', -1);
  params.append('units_wind', unitsWind);
  params.append('units_temp', temperatureUnit(unitsTemp));
  params.append('width', width);
  params.append('height', height);
  appendCommon(params, options);

  return `${widgetUrl}/widgets/web/windArchive?${params.toString()}`;
}

// A fixed-size widget that shows wind only, so unlike the others it takes no width, height or
// temperature unit
export function buildWindStatsUrl(options) {
  const { widgetUrl, unitsWind } = options;
  const params = new URLSearchParams();

  appendLocation(params, options);
  params.append('units_wind', unitsWind);
  appendCommon(params, options);

  return `${widgetUrl}/widgets/web/windStats?${params.toString()}`;
}

// The map widget: a profile's spots, or the spot's area, with map controls and a map type
export function buildWindMapUrl(options) {
  const { widgetUrl, width, height, color, title, profileId, mapType } = options;
  const params = new URLSearchParams({
    w: width,
    h: height,
    c: stripHash(color),
    set_ctl: 'all',
    m_m: 't'
  });

  if (title) params.append('csn', title);

  if (profileId) params.append('pid', profileId);
  else appendMapSearch(params, options);

  if (mapType && mapType !== 'contrast') params.append('m_t', mapType);

  appendMapTail(params, options);

  return `${widgetUrl}/widgets/web/windMap?${params.toString()}`;
}

// Live Wind: the map widget's endpoint in its Live Wind mode (rp=18), without map controls
export function buildLiveWindUrl(options) {
  const { widgetUrl, width, height, color, title } = options;
  const params = new URLSearchParams({
    w: width,
    h: height,
    c: stripHash(color),
    rp: 18,
    m_m: 't'
  });

  if (title) params.append('csn', title);

  appendMapSearch(params, options);
  appendMapTail(params, options);

  return `${widgetUrl}/widgets/web/windMap?${params.toString()}`;
}

// The iframe markup users paste into their site. Only the two map widgets get a name and id
// ("wind-map").
export function buildIframeCode({ src, width, height, name }) {
  const nameAttributes = name ? ` name="${name}" id="${name}"` : '';

  return `<iframe align="top"${nameAttributes} src="${src}" width="${width}" height="${height}" frameborder="0" scrolling="no" allowtransparency="no"></iframe>`;
}
